import React, { useEffect, useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Accordions } from "../../components/shared/Accordion/Accordions";
import VideoEmbed from "../../components/shared/Video/VideoEmbed";
import { FeatureDiscoveryHelpEntries } from "../../components/FeatureDiscovery/FeatureDiscoveryHelpEntries";
import {
  UNITY_COMPLETE_VIDEO_URL,
  UNITY_CONFIRM_VIDEO_URL,
} from "../../components/shared/Video/Video.constants";
import unityConfirmThumbnail from "../../assets/images/unityConfirmThumbnail.jpg";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { helpAccordions, unityConfirmAccordions } from "./HelpData";
import { UnityCompleteResources } from "./UnityCompleteResources";
import { UnityConfirmResources } from "./UnityConfirmResources";
import {
  HelpPanel,
  HelpPanelAccordionCard,
  HelpPanelBody,
  HelpPanelCloseButton,
  HelpPanelHeader,
  HelpPanelOverlay,
  HelpPanelSectionLabel,
  HelpPanelTab,
  HelpPanelTabs,
  HelpPanelTitle,
  HelpPanelVideoCard,
} from "./Help.styles";

type HelpTab = "complete" | "confirm";

interface HelpSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HelpSidePanel = ({
  isOpen,
  onClose,
}: HelpSidePanelProps): React.ReactElement => {
  const [activeTab, setActiveTab] = useState<HelpTab>("complete");
  const panelRef = useRef<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const { isDesktop } = useDeviceHook();
  const { enabled: providerCfcEnabled } = useBooleanFeatureFlag(
    FeatureFlags.ProviderCfc,
  );
  const isUnityConfirmActive = activeTab === "confirm" && providerCfcEnabled;

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    if (isOpen) {
      panel.removeAttribute("inert");
      openerRef.current = document.activeElement as HTMLElement | null;
      closeButtonRef.current?.focus();
      wasOpenRef.current = true;
    } else {
      panel.setAttribute("inert", "");
      if (wasOpenRef.current) {
        openerRef.current?.focus();
        wasOpenRef.current = false;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || isDesktop) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
    };
  }, [isDesktop, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!providerCfcEnabled && activeTab === "confirm") {
      setActiveTab("complete");
    }
  }, [activeTab, providerCfcEnabled]);

  const accordions = useMemo(() => helpAccordions(), []);

  const panelAccordions = useMemo(
    () => (isUnityConfirmActive ? unityConfirmAccordions() : accordions),
    [accordions, isUnityConfirmActive],
  );
  const activeVideoKey = isUnityConfirmActive ? "confirm" : "complete";

  return (
    <>
      <HelpPanelOverlay
        $isOpen={isOpen && !isDesktop}
        data-open={isOpen && !isDesktop ? "true" : "false"}
        data-testid="help-panel-overlay"
        onClick={onClose}
      />
      <HelpPanel
        ref={panelRef}
        id="provider-help-panel"
        role="dialog"
        $isOpen={isOpen}
        aria-modal={isOpen && !isDesktop ? "true" : undefined}
        aria-hidden={!isOpen}
        aria-label="Help and FAQs"
        data-testid="help-side-panel"
      >
        <HelpPanelHeader>
          <HelpPanelTitle>Help & FAQ</HelpPanelTitle>
          <HelpPanelCloseButton
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close Help and FAQs"
          >
            <CloseIcon />
          </HelpPanelCloseButton>
        </HelpPanelHeader>
        <HelpPanelBody data-testid="help-panel-body">
          <FeatureDiscoveryHelpEntries />
          <HelpPanelTabs aria-label="FAQ categories">
            <HelpPanelTab
              type="button"
              aria-pressed={activeTab === "complete"}
              $isActive={activeTab === "complete"}
              onClick={() => setActiveTab("complete")}
            >
              UNITY COMPLETE
            </HelpPanelTab>
            {providerCfcEnabled && (
              <HelpPanelTab
                type="button"
                aria-pressed={activeTab === "confirm"}
                $isActive={activeTab === "confirm"}
                onClick={() => setActiveTab("confirm")}
              >
                UNITY CONFIRM
              </HelpPanelTab>
            )}
          </HelpPanelTabs>
          <HelpPanelVideoCard data-testid="help-panel-video-card">
            {isOpen && (
              <VideoEmbed
                key={activeVideoKey}
                variant="panel"
                videoUrl={
                  isUnityConfirmActive
                    ? UNITY_CONFIRM_VIDEO_URL
                    : UNITY_COMPLETE_VIDEO_URL
                }
                thumbnailImage={
                  isUnityConfirmActive ? unityConfirmThumbnail : undefined
                }
                overlayText={
                  isUnityConfirmActive ? (
                    <>Want to know more about Unity Confirm™?</>
                  ) : (
                    <>Want to know more about Unity Complete®?</>
                  )
                }
              />
            )}
          </HelpPanelVideoCard>
          <HelpPanelSectionLabel>FAQs</HelpPanelSectionLabel>
          <HelpPanelAccordionCard data-testid="help-panel-faq-card">
            <Accordions data={panelAccordions} />
          </HelpPanelAccordionCard>
          {activeTab === "confirm" && providerCfcEnabled ? (
            <UnityConfirmResources />
          ) : (
            <UnityCompleteResources />
          )}
        </HelpPanelBody>
      </HelpPanel>
    </>
  );
};
